import { useCallback, useRef } from 'react';
import { useCurrentPlayerCharacter, useValueStoreSelector } from '@sorrem-client/ui/hooks';
import styles from './SkillsMainPanel.module.scss';
import { AsyncStateMessage } from '@sorrem-client/ui/components';
import { SKILL_TREE_ZOOM_LEVELS, zoomSkillTreeViewAt, type SkillTreeViewStore } from '@sorrem-client/ui/features/SkillTree';
import { ViewControls } from '@sorrem-client/ui/features/ViewControls';
import { toTourTargetProps } from '@sorrem-client/ui/shared/tour-targets';

interface SkillsMainPanelProps {
    /** The tree's pan/zoom, owned by Character Home (the tree itself is a full-screen layer there). */
    readonly viewStore: SkillTreeViewStore;
}

/**
 * The Skills tab's center-panel content. The tree is drawn full screen behind
 * Character Home's panels (see `SkillTree`), so this panel only holds the
 * loading/empty message and the zoom buttons, which zoom about the center of
 * this panel: the open area the tree is framed in.
 */
export function SkillsMainPanel({ viewStore }: SkillsMainPanelProps): JSX.Element {
    const { character: player, isLoading, error } = useCurrentPlayerCharacter();
    const panelRef = useRef<HTMLDivElement>(null);
    // Re-renders as the zoom changes, not as the tree pans.
    const zoom = useValueStoreSelector(viewStore, (view) => view.zoom);

    const handleZoomChange = useCallback((nextZoom: number): void => {
        const rect = panelRef.current?.getBoundingClientRect();
        // The tree layer spans the window, so focus is this panel's center relative to the window's.
        const focus = rect
            ? { x: rect.left + rect.width / 2 - window.innerWidth / 2, y: rect.top + rect.height / 2 - window.innerHeight / 2 }
            : { x: 0, y: 0 };
        viewStore.set((current) => zoomSkillTreeViewAt(current, nextZoom, focus));
    }, [viewStore]);

    return (
        <div ref={panelRef} className={styles.skillsMainPanel} {...toTourTargetProps('skills-panel')}>
            <AsyncStateMessage
                isLoading={isLoading}
                error={error}
                isEmpty={player === null}
                loadingMessage="Loading skills..."
                emptyMessage="No skill data available."
            />

            {error === null && player !== null && (
                <div className={styles.zoomControls}>
                    <ViewControls
                        zoom={zoom}
                        zoomLevels={SKILL_TREE_ZOOM_LEVELS}
                        onZoomChange={handleZoomChange}
                        title="Skill tree zoom"
                    />
                </div>
            )}
        </div>
    );
}
